import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useRequestOtp, useVerifyOtp } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Input } from '../components/Input.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { isSafeRedirect } from '../utils/redirect.js';

type FormState = 'email' | 'code';

export const SignInPage = () => {
  const [formState, setFormState] = useState<FormState>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const redirectTo = isSafeRedirect(from) ? from : undefined;

  const requestOtp = useRequestOtp();
  const { onSignIn } = useAuth();
  const verifyOtp = useVerifyOtp({
    onSuccess: () => {
      onSignIn();
      void navigate(redirectTo ?? '/feeds', { replace: true });
    },
    onError: () => setError('That code is invalid or has expired. Please try again.'),
  });

  if (isAuthenticated) {
    return <Navigate to="/feeds" replace />;
  }

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);

    requestOtp.mutate(
      { email },
      {
        onSuccess: () => setFormState('code'),
        onError: () => {
          setError('Something went wrong. Please try again.');
        },
      },
    );
  };

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    verifyOtp.mutate({ email, code });
  };

  return (
    <>
      <div className="text-center">
        <h1 className="font-serif text-2xl text-text">The Stone Circle</h1>
        <p className="mt-2 text-sm text-text-secondary">Oracle Card Readings</p>
      </div>

      <Card>
        {formState === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="grid gap-4">
            <Input
              label="Email"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
            {error && <p className="text-sm text-error">{error}</p>}
            <Button type="submit" disabled={requestOtp.isPending}>
              {requestOtp.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                'Email me a code'
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit} className="grid gap-4">
            <p className="text-sm text-text-secondary">
              We sent a six-digit code to <strong>{email}</strong>. Enter it below to sign in.
            </p>
            <Input
              label="Sign-in code"
              id="one-time-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              required
              autoFocus
            />
            {error && <p className="text-sm text-error">{error}</p>}
            <Button type="submit" disabled={verifyOtp.isPending || code.length !== 6}>
              {verifyOtp.isPending ? <Spinner size="sm" className="text-white" /> : 'Sign in'}
            </Button>
            <p className="text-xs text-disabled-text">The code expires in 15 minutes.</p>
            <button
              type="button"
              onClick={() => {
                setFormState('email');
                setCode('');
                setError(undefined);
              }}
              className="text-sm text-text-secondary hover:text-text"
            >
              Use a different email or request another code
            </button>
          </form>
        )}
      </Card>
    </>
  );
};
